/**
 * invoiceExtras — dispatch details on a Quick Bill, kept in the invoice's
 * existing Terms field.
 *
 * ── Why Terms ───────────────────────────────────────────────────────────────
 * Delivery note, buyer's order no., dispatched through, destination, vehicle
 * no. and ship-to have no columns on `invoices`, and the schema is locked. The
 * owner chose (2026-09-29) to keep them as labelled lines in
 * `invoices.terms_conditions` rather than change the schema. That column
 * already syncs to every app and already prints on the invoice, so nothing
 * downstream needs to change to carry them.
 *
 * The cost, accepted: they are text, not fields — not searchable or reportable
 * on their own. The format below is fixed so they can at least be read back
 * reliably ("Label: value" per line, a blank line, then the free terms).
 *
 * Pure.
 */

export const DISPATCH_FIELDS = [
  { key: 'deliveryNote', label: 'Delivery note' },
  { key: 'deliveryNoteDate', label: 'Delivery note date' },
  { key: 'buyerOrderNo', label: "Buyer's order no." },
  { key: 'buyerOrderDate', label: "Buyer's order date" },
  { key: 'dispatchDocNo', label: 'Dispatch doc no.' },
  { key: 'dispatchedThrough', label: 'Dispatched through' },
  { key: 'vehicleNo', label: 'Vehicle no.' },
  { key: 'destination', label: 'Destination' },
  { key: 'termsOfDelivery', label: 'Terms of delivery' },
  { key: 'shipTo', label: 'Ship to' },
] as const;

export type DispatchKey = (typeof DISPATCH_FIELDS)[number]['key'];
export type DispatchDetails = Partial<Record<DispatchKey, string>>;

/** One line per value: newlines inside a value would break the format. */
const oneLine = (v: string) => v.replace(/\s*[\r\n]+\s*/g, ', ').trim();

/**
 * The Terms text to store: filled dispatch fields as "Label: value" lines, a
 * blank line, then the free-form terms. Empty parts are left out; nothing at
 * all gives ''.
 */
export function composeTerms(details: DispatchDetails, freeTerms = ''): string {
  const lines = DISPATCH_FIELDS
    .map(({ key, label }) => {
      const v = oneLine(details[key] ?? '');
      return v ? `${label}: ${v}` : null;
    })
    .filter((l): l is string => l !== null);
  const free = freeTerms.trim();
  if (lines.length === 0) return free;
  return free ? `${lines.join('\n')}\n\n${free}` : lines.join('\n');
}

const BY_LABEL = new Map(DISPATCH_FIELDS.map((f) => [f.label.toLowerCase(), f.key]));

/**
 * Inverse of {@link composeTerms}. Leading "Label: value" lines with a known
 * label become details; everything from the first other line on is free terms.
 * Text written by hand (no known labels) comes back entirely as free terms.
 */
export function parseTerms(text: string | null | undefined): { details: DispatchDetails; freeTerms: string } {
  const details: DispatchDetails = {};
  const lines = (text ?? '').replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  for (; i < lines.length; i++) {
    const m = /^([^:]+):\s*(.*)$/.exec(lines[i]);
    const key = m ? BY_LABEL.get(m[1].trim().toLowerCase()) : undefined;
    if (!m || !key) break;
    details[key] = m[2].trim();
  }
  return { details, freeTerms: lines.slice(i).join('\n').trim() };
}
